import { Flex } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { Campaign, Targeting } from '@feathr/blackbox';
import { useStickyCard } from '@feathr/components';
import { useCampaignChannelVisibility } from '@feathr/extender/hooks';
import type { ListResponse } from '@feathr/rachis';
import { TargetAudienceOverview, useAudienceBreakdown } from '@feathr/report_components';

import { type IUniqueEmailsResult } from '../AddWizard.useReconcileUniqueEmails';
import AdWizardAudienceStepContent from './AdWizardAudienceStep.Content';
import NewAudience from './AdWizardAudienceStep.NewAudience';
import {
  getMetaLookalikeLabel,
  valueToSlider,
} from './LookalikeAudienceTargeting/LookalikeAudienceTargeting.utils';

import * as styles from './AdWizardAudienceStep.css';

interface IProps {
  campaign: Campaign;
  targetings: ListResponse<Targeting>;
  emailValidation: IUniqueEmailsResult;
}

function AdWizardAudienceStep({
  campaign,
  targetings: targetingsList,
  emailValidation,
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const { showDisplay, showMeta } = useCampaignChannelVisibility(campaign);
  const audienceData = useAudienceBreakdown({
    targetings: targetingsList,
    campaign,
    showMeta,
  });

  const sliderPosition = audienceData.lookalikeAudienceSize
    ? valueToSlider(audienceData.lookalikeAudienceSize)
    : undefined;
  const metaLookalikeLabel = getMetaLookalikeLabel(sliderPosition, t);

  const { isOmniCampaign, isOmniReachCampaign } = campaign;

  const { leftContentRef, stickyWrapperHeight } = useStickyCard({
    enabled: isOmniCampaign,
    dependencies: [targetingsList, audienceData],
  });

  const stepContent = (
    <AdWizardAudienceStepContent
      campaign={campaign}
      emailValidation={emailValidation}
      targetingsList={targetingsList}
    />
  );

  const wrappedStepContent = isOmniCampaign ? (
    <div className={styles.left} ref={leftContentRef}>
      {stepContent}
    </div>
  ) : (
    stepContent
  );

  return (
    <Flex className={styles.root} justify={isOmniCampaign ? 'space-between' : 'center'}>
      {wrappedStepContent}
      {isOmniCampaign && !isOmniReachCampaign && (
        <div
          className={styles.stickyWrapper}
          style={{
            // Dynamic height controls when sticky element "unsticks" and becomes static.
            // Height = left content height + buffer, so right column stays sticky while
            // scrolling through left content, then unsticks near the end for alignment.
            height: stickyWrapperHeight,
          }}
        >
          <TargetAudienceOverview
            className={styles.stickyRight}
            metaLookalikeLabel={metaLookalikeLabel}
            showDisplay={showDisplay}
            showMeta={showMeta}
            width={'narrow'}
            {...audienceData}
          />
        </div>
      )}

      {isOmniReachCampaign && (
        <div
          className={styles.stickyWrapper}
          style={{
            // Dynamic height controls when sticky element "unsticks" and becomes static.
            // Height = left content height + buffer, so right column stays sticky while
            // scrolling through left content, then unsticks near the end for alignment.
            height: stickyWrapperHeight,
          }}
        >
          <NewAudience
            campaign={campaign}
            className={styles.stickyRight}
            targetings={targetingsList}
          />
        </div>
      )}
    </Flex>
  );
}

export default observer(AdWizardAudienceStep);
